import React from "react";
import { Text } from "react-native";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";

/**
 * Component to display client's week status with optional colored highlighting
 * for unprepared weeks.
 *
 * @param {Object} client - The client object with block week information
 * @param {Object} style - Style object to apply to the Text component
 * @param {number} numberOfLines - Optional number of lines to display
 * @param {boolean} showColorForUnprepared - Whether to show red color for unprepared weeks (default: false)
 * @param {string} tagColor - Custom color for the red tags (default: "#C75450")
 */
const ClientWeekStatusText = observer(
  ({
    client,
    style = {},
    numberOfLines,
    showColorForUnprepared = false,
    tagColor = "#C75450",
  }) => {
    const { t } = useTranslation();

    // No active block
    if (client.getBlockCurrentWeek === 0 && client.getBlockWeeks === 0) {
      return (
        <Text style={style} numberOfLines={numberOfLines}>
          {t("withoutActiveBlock-string")}
        </Text>
      );
    }

    // Week is prepared
    if (client.getBlockCurrentWeekPrepared) {
      return (
        <Text style={style} numberOfLines={numberOfLines}>
          {t("clientWeekStatusPrepared-string", {
            blockCurrentWeek: client.getBlockCurrentWeek,
            blockWeeks: client.getBlockWeeks,
          })}
        </Text>
      );
    }

    // Week is unprepared
    if (showColorForUnprepared) {
      const text = t("clientWeekStatusUnprepared-string", {
        blockCurrentWeek: client.getBlockCurrentWeek,
        blockWeeks: client.getBlockWeeks,
      });
      const parts = text.split(/<red>|<\/red>/);

      return (
        <Text style={style} numberOfLines={numberOfLines}>
          {parts.map((part, index) => {
            // Odd indices are content inside <red> tags
            const isColored = index % 2 === 1;

            return isColored ? (
              <Text key={index} style={{ color: tagColor }}>
                {part}
              </Text>
            ) : (
              part
            );
          })}
        </Text>
      );
    }

    // Default: unprepared without color (fallback to basic status)
    return (
      <Text style={style} numberOfLines={numberOfLines}>
        {t("clientWeekStatus-string", {
          blockCurrentWeek: client.getBlockCurrentWeek,
          blockWeeks: client.getBlockWeeks,
        })}
      </Text>
    );
  }
);

export default ClientWeekStatusText;
